import { useCallback, useEffect, useRef, useState } from 'react';

export type FlashTone = 'success' | 'error';

export interface FlashMessage {
  type: FlashTone;
  text: string;
}

/** Long enough to read a sentence, short enough not to sit under the next thing you do. */
const DISMISS_AFTER_MS = 5_000;

/**
 * The transient "saved" / "that did not work" line at the top of a screen.
 *
 * Eight master screens each held this as their own `flashMsg` state plus a four-line `flash`
 * helper, and every one of them had the same bug: the dismissal timer was started and never
 * cancelled. Flash a message, flash a second one four seconds later, and the first message's timer
 * fires at five seconds and clears the second after one — so the message that mattered least stayed
 * longest. Cancelling the outstanding timer on each new message fixes it in one place.
 *
 * The timer is also cleared on unmount, so a screen left during those five seconds does not wake up
 * to set state on a component that no longer exists.
 */
export function useFlashMessage(dismissAfterMs: number = DISMISS_AFTER_MS) {
  const [message, setMessage] = useState<FlashMessage | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const stopTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = undefined;
  };

  const flash = useCallback((type: FlashTone, text: string) => {
    stopTimer();
    setMessage({ type, text });
    timer.current = setTimeout(() => setMessage(null), dismissAfterMs);
  }, [dismissAfterMs]);

  /** Take the message down now — after a refresh, say, where it no longer describes the screen. */
  const clearFlash = useCallback(() => {
    stopTimer();
    setMessage(null);
  }, []);

  useEffect(() => stopTimer, []);

  return { flashMsg: message, flash, clearFlash };
}
